import { useCallback } from 'react'

import useLeadLogin from './useLeadLogin'
import useVerifyAcceptedLead from './useVerifyAcceptedLead'

import AuthService from 'modules/auth/service'
import TransmissionService from 'modules/transmission/service'
import { ERROR } from 'common/constants'
import { useLeadDataContext } from 'modules/lead/context'
import { useTransmissionContext } from 'modules/transmission/context'
import { LeadData } from 'modules/lead/types'

const useFetchLeadByToken = () => {
  const { setIsLoading, setData, setShowConfirm } = useLeadDataContext()
  const { transmissionData: { code }, isPrivate, isClub } = useTransmissionContext()

  const { clearLogin } = useLeadLogin()

  const verifyAcceptedLead = useVerifyAcceptedLead()

  const getProfileProps = useCallback(async (lead: LeadData) => {
    try {
      const { thumborUrlPhoto, urlPhoto, tradeName } = await AuthService.getProfile() || {}

      return {
        photo: thumborUrlPhoto || urlPhoto || void 0,
        name: tradeName || lead.name
      }
    } catch (error) {
      console.error(error)
      return void 0
    }
  }, [])

  const shouldFetchToken = useCallback(() => {
    if (!code) {
      return false
    }

    const token = AuthService.getToken()

    if (!(token || isPrivate)) {
      setIsLoading(false)
      return false
    }

    return true
  }, [code, isPrivate, setIsLoading])

  const fetchLeadByToken = useCallback(async () => {
    if (!shouldFetchToken()) {
      return
    }

    setIsLoading(true)

    try {
      const token = AuthService.getToken()

      if (!token) {
        if (isPrivate) {
          AuthService.logout()
          return
        }

        throw ERROR.EMPTY_TOKEN
      }

      const response = AuthService.getUser()

      let lead: LeadData = {
        name: response.name,
        email: response.email,
        schedule: code,
        userPlatformId: response.id
      }

      if (isPrivate && !(await TransmissionService.verifyAuthorizathion(code))) {
        return
      }

      lead = {
        ...lead,
        ...(await verifyAcceptedLead(lead.name, lead.email)),
        ...(await getProfileProps(lead))
      }

      !lead.accepted && !isClub && setShowConfirm(true)

      setData(prevState => ({
        ...prevState,
        ...lead
      }))
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      clearLogin()
    }
  }, [
    code,
    isPrivate,
    setData,
    setIsLoading,
    clearLogin,
    getProfileProps,
    shouldFetchToken,
    verifyAcceptedLead,
    setShowConfirm,
    isClub
  ])

  return fetchLeadByToken
}

export default useFetchLeadByToken
